import { useMemo } from 'react';

import useCurrency from 'hooks/redux/useCurrency';
import usePreferredNutrientIds from 'hooks/redux/usePreferredNutrientIds';
import useAirNavigation from 'hooks/useAirNavigation';
import * as Search from 'types/Accessory/Search/Search';

export function useAccessorySearchUrl(archive: boolean) {
	const { params, navigate } = useAirNavigation();
	const preferredNutrientIds = usePreferredNutrientIds();
	const currencyCode = useCurrency();

	const search = useMemo(
		() => Search.getFromUrl(params, preferredNutrientIds, currencyCode, archive),
		[params.toString(), archive]
	);

	function createReducer<T>(
		fun: (search: Search.Type, input: T) => Search.Type
	): (input: T) => void {
		return (input: T) => {
			const newSearch: Search.Type = fun(search, input);
			navigate(Search.mapToUrlParamsClass(newSearch));
		};
	}

	function createNoArgReducer(fun: (search: Search.Type) => Search.Type): () => void {
		return () => {
			const newSearch: Search.Type = fun(search);
			navigate(Search.mapToUrlParamsClass(newSearch));
		};
	}

	function createReducerAsync<T>(
		fun: (search: Search.Type, input: T) => Promise<Search.Type>
	): (input: T) => Promise<void> {
		return async (input: T) => {
			const newSearch: Search.Type = await fun(search, input);
			navigate(Search.mapToUrlParamsClass(newSearch));
		};
	}

	return {
		search,
		searchHandlers: {
			archive: createReducerAsync(Search.archive),
			changeCategories: createReducer(Search.changeCategories),
			changeCharacteristics: createReducer(Search.changeCharacteristics),
			changeCo2Labels: createReducer(Search.changeCo2Labels),
			changePageIndex: createReducer(Search.changePageIndex),
			changePageSize: createReducer(Search.changePageSize),
			changeSearchTerm: createReducer(Search.changeSearchTerm),
			changeSlider: createReducer(Search.changeSlider),
			changeSortOrder: createReducer(Search.changeSortOrder),
			changeTags: createReducer(Search.changeTags),
			reset: createNoArgReducer(Search.reset),
			restore: createReducerAsync(Search.restore),
		},
	};
}
